import { useCallback, useEffect, useMemo, useRef } from "react";
import { FieldErrors, FieldValues, UseFormReturn } from "react-hook-form";
import { scrollFirstFormError, ScrollFirstFormErrorOptions } from "../utils";

/// Hook returning a callback that scrolls the nearest out-of-viewport error (bi-directional minimal scroll).
export interface UseScrollFirstErrorExtra {
    /** Auto-trigger when RHF submitCount increments and errors exist (uses minimal scroll util). */
    auto?: boolean;
    /** Limit auto-trigger to the very first failed submit (submitCount===1). */
    firstSubmitOnly?: boolean;
    /** Filter by severities (data-error-severity) before choosing a target. */
    severities?: string[];
}

export function useScrollFirstError<TFieldValues extends FieldValues = FieldValues>(
    formOrErrors: Pick<UseFormReturn<TFieldValues>, "formState"> | FieldErrors<TFieldValues> | undefined,
    options?: ScrollFirstFormErrorOptions, // optional: omit or leave root undefined to no-op (safe early render)
    extra?: UseScrollFirstErrorExtra,
): () => boolean {
    const errors: FieldErrors<TFieldValues> | undefined = (formOrErrors as any)?.formState?.errors || formOrErrors;
    const submitCount: number | undefined = (formOrErrors as any)?.formState?.submitCount;

    const mergedOptions: ScrollFirstFormErrorOptions | undefined = useMemo(() => {
        if (!options) {
            if (extra?.severities) return { severities: extra.severities } as ScrollFirstFormErrorOptions;
            return undefined;
        }
        return { ...options, severities: extra?.severities ?? options.severities };
    }, [options, extra?.severities]);

    const scrollFn = useCallback(() => {
        if (!mergedOptions) return false; // root or options not ready yet
        return scrollFirstFormError(errors as FieldErrors, mergedOptions);
    }, [errors, mergedOptions]);

    const lastSubmitRef = useRef<number>(submitCount ?? 0);
    useEffect(() => {
        if (!extra?.auto) return;
        if (submitCount == null) return; // not provided a form state
        if (submitCount > lastSubmitRef.current) {
            lastSubmitRef.current = submitCount;
            if (extra?.firstSubmitOnly && submitCount > 1) return;
            if (submitCount > 0 && errors && Object.keys(errors).length > 0) {
                scrollFn();
            }
        }
    }, [submitCount, errors, scrollFn, extra?.auto, extra?.firstSubmitOnly]);

    return scrollFn;
}
